'use client';

import Link from 'next/link';
import Image from 'next/image';
import {usePathname} from 'next/navigation';
import {useContext, useEffect, useRef, useState} from 'react';

import CSS from './Navbar.module.scss';
import URLs from './URLs';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import ProfileDropDown from './ProfileDropDown';
import Focus from '@/components/elements/Focus';
import Button from '@/components/elements/Button';
import Vector from '@/components/elements/vector/Vector';
import Notifications, {
  getNormalizedNotificationsCount
} from './notifications/Notifications';
import {LoadingState} from '@/types';
import {scrollInto} from '@/lib/utils';
import {useClickOut} from '@/hooks/click-out';
import {useWindowSize} from '@/hooks/window-size';
import {UserDataContext} from '@/context/UserData';
import {useHideScrollbar} from '@/hooks/hide-scrollbar';
import {resolveAPIRoute, resolveRoute} from '@/lib/routes';
import {
  isProcessing,
  isVerified,
  isRejected
} from '@/app/(authentication)/identity-verification/utils';

export default function Navbar() {
  const {userData, isAuthenticated} = useContext(UserDataContext)!;
  const pathname = usePathname();
  const windowSize = useWindowSize();

  const [activeDropDown, setActiveDropDown] = useState('');
  const [avatarLoadingState, setAvatarLoadingState] = useState(
    LoadingState.Unknown
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const activeDropDownRef = useRef<HTMLUListElement>(null);
  const isStatePopped = useRef(false);

  const isSidebarsOpen = isSidebarOpen || isNotificationsOpen;
  const isAvatarLoaded = avatarLoadingState === LoadingState.Loaded;
  const profile = userData?.profile;
  const status = profile?.status;
  const avatar = profile?.avatar;
  const hasStatus = !!status;
  const hasAvatar = !!avatar;
  const _isProcessing = hasStatus && isProcessing(status);
  const _isVerified = hasStatus && isVerified(status);
  const _isPartiallyVerified = hasStatus && isVerified(status, false);
  const _isPartiallyRejected = hasStatus && isRejected(status, false);
  const avatarPath = hasAvatar
    ? {id: avatar.split('/').slice(-1)[0]}
    : undefined;

  // Closing the drop down menu on outside click
  useClickOut(activeDropDownRef, !!activeDropDown, () => setActiveDropDown(''));

  // Temporarily hiding the scrollbar
  useHideScrollbar(isSidebarsOpen, isNotificationsOpen ? 'Mobile' : undefined);
  useEffect(() => {
    if (
      !windowSize['MobileSmall'] &&
      !windowSize['Mobile'] &&
      !windowSize['MobileWide'] &&
      !windowSize['MobileBig'] &&
      !windowSize['DesktopSmall']
    )
      setIsSidebarOpen(false);
  }, [windowSize]);

  // Adding `box-shadow` on scroll down
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([event]) =>
        event.target.toggleAttribute('data-stuck', event.intersectionRatio < 1),
      {threshold: [1]}
    );
    observer.observe(ref.current!);

    const element = ref.current;
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  /**
   * Handling the scroll issue while preserving the scroll position
   * on navigation change with the browser's back and forward buttons
   * to ensure clicking on the links scrolls the page to the top with
   * the sticky positioned navbar.
   * @see https://github.com/vercel/next.js/discussions/64435
   */
  useEffect(() => {
    const onPopState = () => (isStatePopped.current = true);

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  useEffect(() => {
    if (!isStatePopped.current) {
      // Navigation occurred without pressing
      // the browser's back or forward buttons
      scrollInto(ref);
    } else {
      isStatePopped.current = false;
    }
  }, [pathname]);

  function onClick() {
    onActiveDropDown('profile');
  }

  function onActiveDropDown(dropDown: string) {
    setActiveDropDown(state => (state === dropDown ? '' : dropDown));
  }

  return (
    <>
      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        activeDropDown={activeDropDown}
        activeDropDownRef={activeDropDownRef}
        onActiveDropDown={onActiveDropDown}
      />

      {isAuthenticated && (
        <Notifications
          isOpen={isNotificationsOpen}
          setIsOpen={setIsNotificationsOpen}
          setUnreadNotificationsCount={setUnreadNotificationsCount}
        />
      )}

      <nav ref={ref} className={CSS.navbar}>
        <div>
          <Vector
            name="IoMdMenu"
            className={CSS.menu}
            svgProps={{size: '2.8rem'}}
            onClick={() => setIsSidebarOpen(state => !state)}
            hasFocus
          />

          <Link href={resolveRoute('base')}>
            <Vector name="logo" className={CSS.logo} />
            <Vector name="logoSmall" className={CSS.logoSmall} />
          </Link>

          <URLs
            activeDropDown={activeDropDown}
            activeDropDownRef={activeDropDownRef}
            onActiveDropDown={onActiveDropDown}
            showSubIcons
          />
        </div>

        <div>
          <ThemeToggle />

          {isAuthenticated ? (
            <>
              <Vector
                name="HiBell"
                svgProps={{size: '2rem'}}
                onClick={() => setIsNotificationsOpen(state => !state)}
                className={unreadNotificationsCount ? CSS.badge : undefined}
                data-badge={getNormalizedNotificationsCount(
                  unreadNotificationsCount
                )}
                hasRoundBorder
                hasFocus
              />

              <div
                className={[CSS.avatar, isSidebarsOpen ? '' : CSS.hover].join(
                  ' '
                )}>
                <>
                  {(!hasAvatar || (hasAvatar && !isAvatarLoaded)) && (
                    <Vector
                      name="IoPerson"
                      onClick={onClick}
                      svgProps={{size: '2rem'}}
                      hasRoundBorder
                      hasFocus
                    />
                  )}
                  {hasAvatar && (
                    <Focus>
                      <Image
                        src={resolveAPIRoute(
                          'static.assets.avatars.id',
                          avatarPath
                        ).toString()}
                        className={isAvatarLoaded ? CSS.loaded : undefined}
                        onClick={onClick}
                        onLoad={() =>
                          setAvatarLoadingState(LoadingState.Loaded)
                        }
                        width={128}
                        height={128}
                        draggable={false}
                        loading="eager"
                        alt=""
                      />
                    </Focus>
                  )}
                  {hasStatus && !_isVerified && (
                    <div
                      data-badge="!"
                      className={[
                        CSS.badge,
                        _isProcessing
                          ? CSS.positive
                          : _isPartiallyVerified && !_isPartiallyRejected
                          ? CSS.special
                          : ''
                      ].join(' ')}
                    />
                  )}
                </>
              </div>
            </>
          ) : (
            <Link href={resolveRoute('base.login')} tabIndex={-1}>
              <Button isSpecial>ورود | ثبت نام</Button>
            </Link>
          )}

          {/* Moving the element to a different position
          will break the hover functionality. */}
          <ProfileDropDown
            isOpen={activeDropDown === 'profile'}
            isProcessing={_isProcessing}
            isVerified={_isVerified}
            isPartiallyVerified={_isPartiallyVerified}
            isPartiallyRejected={_isPartiallyRejected}
          />
        </div>
      </nav>
    </>
  );
}
